// admin/src/scenes/dashboard/index.jsx
// ─────────────────────────────────────────────────────────────────
// Changes from original:
//   1. Added Reviews stat box (total reviews + average rating)
//   2. Added Testimonials stat box (pending approvals)
//   3. Both fetched alongside existing dashboard-stats call
// ─────────────────────────────────────────────────────────────────
import {
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../../theme";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailIcon from "@mui/icons-material/Email";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StarIcon from "@mui/icons-material/Star";
import RateReviewIcon from "@mui/icons-material/RateReview";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import BarChart from "../../components/BarChart";
import StatBox from "../../components/StatBox";
import ProgressCircle from "../../components/ProgressCircle";
import { useEffect, useState } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const BOOK_COLORS = ["#22c55e", "#3b82f6", "#f59e0b", "#a855f7", "#ef4444"];

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [stats, setStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [barData, setBarData] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0 });
  const [pendingTestimonials, setPendingTestimonials] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("adminToken");
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch all data in parallel
        const [dashRes, reviewsRes, testimonialsRes] = await Promise.allSettled(
          [
            axios.get(`${BASE_URL}/api/admin/dashboard-stats`, { headers }),
            axios.get(`${BASE_URL}/api/admin/reviews`, { headers }),
            axios.get(`${BASE_URL}/api/admin/testimonials?status=pending`, {
              headers,
            }),
          ],
        );

        // ── Main dashboard stats ───────────────────────────────
        if (dashRes.status === "fulfilled" && dashRes.value.data?.success) {
          const {
            stats: apiStats = {},
            recentTransactions: apiTx = [],
            revenueByDay = [],
            salesByBook = [],
          } = dashRes.value.data;

          setStats(apiStats);
          setRecentTransactions(apiTx);

          setLineData([
            {
              id: "Revenue (KES)",
              color: "#22c55e",
              data: revenueByDay.map((d) => ({
                x: d._id?.slice(5) || d._id,
                y: Number(d.revenue || 0),
              })),
            },
          ]);

          setBarData(
            salesByBook.map((book, i) => {
              const title = book?._id || "Untitled";
              return {
                label: title,
                category:
                  title.length > 18 ? `${title.slice(0, 18)}...` : title,
                sales: Number(book?.sales || 0),
                color: BOOK_COLORS[i % BOOK_COLORS.length],
              };
            }),
          );
        } else {
          setStats({
            emailsSent: 0,
            totalRevenue: 0,
            totalSales: 0,
            totalCustomers: 0,
          });
        }

        // ── Review stats ───────────────────────────────────────
        if (reviewsRes.status === "fulfilled") {
          const reviews = reviewsRes.value.data.reviews || [];
          const total = reviews.length;
          const avg = total
            ? reviews.reduce((sum, r) => sum + r.rating, 0) / total
            : 0;
          setReviewStats({ total, average: Math.round(avg * 10) / 10 });
        }

        // ── Pending testimonials ───────────────────────────────
        if (testimonialsRes.status === "fulfilled") {
          setPendingTestimonials(
            testimonialsRes.value.data.testimonials?.length || 0,
          );
        }
      } catch (error) {
        console.error("Dashboard fetch error:", error);
        setStats({
          emailsSent: 0,
          totalRevenue: 0,
          totalSales: 0,
          totalCustomers: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
        <Button
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
          }}
        >
          <DownloadOutlinedIcon sx={{ mr: "10px" }} />
          Download Reports
        </Button>
      </Box>

      {/* GRID */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
      >
        {/* ROW 1 — STAT BOXES (6 total now: original 4 + reviews + testimonials) */}
        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={stats?.emailsSent?.toString() || "0"}
            subtitle="Emails Sent"
            progress={Math.min((stats?.emailsSent || 0) / 100, 1).toFixed(2)}
            increase={`${stats?.emailsSent || 0} total`}
            icon={
              <EmailIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={`KES ${stats?.totalRevenue?.toLocaleString() || "0"}`}
            subtitle="Revenue Generated"
            progress={Math.min((stats?.totalRevenue || 0) / 100000, 1).toFixed(
              2,
            )}
            increase={`${stats?.totalSales || 0} sales`}
            icon={
              <AttachMoneyIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={stats?.totalSales?.toString() || "0"}
            subtitle="Books Sold"
            progress={Math.min((stats?.totalSales || 0) / 500, 1).toFixed(2)}
            increase={`${stats?.totalSales || 0} completed`}
            icon={
              <PointOfSaleIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={stats?.totalCustomers?.toString() || "0"}
            subtitle="Unique Customers"
            progress={Math.min((stats?.totalCustomers || 0) / 200, 1).toFixed(
              2,
            )}
            increase={`${stats?.totalCustomers || 0} customers`}
            icon={
              <PersonAddIcon
                sx={{ color: colors.greenAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        {/* ── NEW: Reviews stat ── */}
        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={reviewStats.total.toString()}
            subtitle="Book Reviews"
            progress={Math.min(reviewStats.total / 50, 1).toFixed(2)}
            increase={
              reviewStats.total > 0
                ? `Avg ${reviewStats.average}★`
                : "No reviews yet"
            }
            icon={<StarIcon sx={{ color: "#f59e0b", fontSize: "26px" }} />}
          />
        </Box>

        {/* ── NEW: Testimonials pending ── */}
        <Box
          gridColumn="span 2"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={pendingTestimonials.toString()}
            subtitle="Pending Testimonials"
            progress={Math.min(pendingTestimonials / 10, 1).toFixed(2)}
            increase={pendingTestimonials > 0 ? "Need review" : "All clear"}
            icon={
              <RateReviewIcon
                sx={{
                  color:
                    pendingTestimonials > 0
                      ? "#f59e0b"
                      : colors.greenAccent[600],
                  fontSize: "26px",
                }}
              />
            }
          />
        </Box>

        {/* ROW 2 — LINE CHART + RECENT TRANSACTIONS */}
        <Box
          gridColumn="span 8"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
        >
          <Box
            mt="25px"
            p="0 30px"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                color={colors.grey[100]}
              >
                Revenue Trend (Last 30 Days)
              </Typography>
              <Typography
                variant="h3"
                fontWeight="bold"
                color={colors.greenAccent[500]}
              >
                KES {stats?.totalRevenue?.toLocaleString() || "0"}
              </Typography>
            </Box>
            <IconButton>
              <DownloadOutlinedIcon
                sx={{ fontSize: "26px", color: colors.greenAccent[500] }}
              />
            </IconButton>
          </Box>
          <Box height="250px" m="-20px 0 0 0">
            {lineData.length > 0 ? (
              <LineChart isDashboard={true} data={lineData} />
            ) : (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography color={colors.grey[300]}>
                  No revenue trend data yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          overflow="auto"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`4px solid ${colors.primary[500]}`}
            p="15px"
          >
            <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
              Recent Transactions
            </Typography>
          </Box>
          {recentTransactions.length === 0 ? (
            <Box p="15px">
              <Typography color={colors.grey[300]}>
                No transactions yet.
              </Typography>
            </Box>
          ) : (
            recentTransactions.slice(0, 8).map((tx, i) => (
              <Box
                key={`${tx.transactionId}-${i}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom={`4px solid ${colors.primary[500]}`}
                p="15px"
              >
                <Box>
                  <Typography
                    color={colors.greenAccent[500]}
                    variant="h5"
                    fontWeight="600"
                  >
                    {tx.transactionId || "N/A"}
                  </Typography>
                  <Typography color={colors.grey[100]}>
                    {tx.payerName}
                  </Typography>
                </Box>
                <Box color={colors.grey[100]} fontSize="12px">
                  {new Date(tx.createdAt).toLocaleDateString()}
                </Box>
                <Box
                  backgroundColor={colors.greenAccent[500]}
                  p="5px 10px"
                  borderRadius="4px"
                  color={colors.primary[900]}
                  fontWeight="bold"
                >
                  KES {tx.amount}
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* ROW 3 — PROGRESS + BAR CHART + TOP BOOKS */}
        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          p="30px"
        >
          <Typography variant="h5" fontWeight="600">
            Sales Progress
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt="25px"
          >
            <ProgressCircle size="125" />
            <Typography
              variant="h5"
              color={colors.greenAccent[500]}
              sx={{ mt: "15px" }}
            >
              KES {stats?.totalRevenue?.toLocaleString() || "0"} revenue
            </Typography>
            <Typography color={colors.grey[300]}>
              Total from all completed payments
            </Typography>
          </Box>
        </Box>

        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ padding: "30px 30px 0 30px" }}
          >
            Sales by Book
          </Typography>
          <Box height="250px" mt="-20px">
            {barData.length > 0 ? (
              <BarChart isDashboard={true} data={barData} />
            ) : (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography color={colors.grey[300]}>
                  No book sales data yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          padding="30px"
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{ marginBottom: "15px" }}
          >
            Top Selling Books
          </Typography>
          {barData.length === 0 ? (
            <Typography color={colors.grey[300]}>No sales data yet.</Typography>
          ) : (
            barData.map((book, i) => (
              <Box
                key={i}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="12px"
              >
                <Box display="flex" alignItems="center" gap="10px">
                  <Box
                    width="10px"
                    height="10px"
                    borderRadius="50%"
                    backgroundColor={book.color || colors.greenAccent[500]}
                  />
                  <Typography color={colors.grey[100]} fontSize="13px">
                    {book.label}
                  </Typography>
                </Box>
                <Typography color={colors.greenAccent[400]} fontWeight="600">
                  {book.sales} sold
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
